import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Globe, Check } from 'lucide-react';
import * as Flags from 'country-flag-icons/react/3x2';
import { useLanguage } from '../contexts/LanguageContext';

// Ülke koduna göre SVG bayrak; bulunamazsa Globe ikonu.
const Flag = ({ countryCode, className }) => {
  const Component = countryCode ? Flags[countryCode.toUpperCase()] : null;
  if (!Component) return <Globe className={className} />;
  return <Component className={className} title={countryCode} />;
};

const LanguageSelector = () => {
  const { supportedLanguages, currentLang, setLanguage, hasMultipleLanguages, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Dışarı tıklayınca kapat.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Tek dil (veya hiç) varsa seçici gösterilmez.
  if (!hasMultipleLanguages) return null;

  const active = supportedLanguages.find((l) => l.code === currentLang);

  const handleSelect = (code) => {
    setLanguage(code);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-3 py-2 rounded-xl border border-gray-700 transition-colors"
        aria-label={t('selectLanguage')}
        title={t('selectLanguage')}
      >
        <Flag countryCode={active?.countryCode} className="h-4 w-6 rounded-sm object-cover" />
        <span className="text-sm font-medium hidden sm:inline">
          {active?.nativeName || active?.code?.toUpperCase() || ''}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl py-2 z-50">
          {supportedLanguages.map((lang) => {
            const isActive = lang.code === currentLang;
            return (
              <button
                key={lang.code}
                onClick={() => handleSelect(lang.code)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                  isActive ? 'bg-orange-600/20' : 'hover:bg-gray-700'
                }`}
              >
                <Flag countryCode={lang.countryCode} className="h-5 w-7 rounded-sm object-cover flex-shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-white truncate">{lang.nativeName || lang.code}</span>
                  <span className="block text-xs text-gray-400 truncate">{lang.name}</span>
                </span>
                {isActive && <Check className="h-4 w-4 text-orange-400 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
